import { formatDate, formatCurrency, formatDecimalValues } from "@/utils/helpers";

// A settled document legitimately reports a balance of 0. Truthiness checks blank those cells out
// (0 and -0 are both falsy), so balance columns must test for absence rather than for falsiness.
const isBlank = (value) => value === null || value === undefined || value === "";

const formatMoney = (value) => (isBlank(value) ? "" : formatCurrency(String(value)));

// Money in the invoices grid stays numeric all the way to the cell, formatted here instead of
// being pre-stringified by the caller. That is what lets the group footers total the columns —
// and `summable` below is what tells them which columns to total.
const formatAmount = (value) => (isBlank(value) ? "" : formatDecimalValues(Number(value)));

const formatDateCell = (value) => (isBlank(value) ? "" : formatDate(new Date(value)));

// Document references arrive from the report already joined into one comma-separated string --
// a line settled by three receipts names all three. Rendered as-is rather than truncated: the
// column is resizable, and a half-shown document number is worse than a wrapped one.
const formatRefs = (value) => (isBlank(value) ? "" : String(value));

// The statement of account: receivable movements, plus credit notes the ledger has not applied,
// which carry their value in Credit and so move the running balance like any other row.
//
// Cheques on hand used to ride along as informational rows whose value sat in Amount / Variance
// columns. A collected cheque has deliberately not reduced the balance, so it is a disclosure
// rather than a movement and now has its own block below — and those columns went with it, along
// with Document Balance, which reported an invoice's balance as it stands today rather than as at
// the statement date and so contradicted the Running Balance beside it on a back-dated run.
// Widths are explicit rather than semantic. estimatedColumnWidth() buckets by name, which put both
// Document Number and Description on 350px and pushed the statement to 1850px -- so Debit, Credit,
// Balance and Running Balance, the columns that make it a statement, all sat off the right edge.
// These add to 1140px, which fits the ~1158px grid pane left by the sidebar at a 1500px window, so
// the money columns are on screen at the width finance actually runs. Text columns absorbed the
// squeeze and the money columns did not: an amount that does not fit is unreadable, whereas a
// truncated description is merely abbreviated -- and every column is still resizable.
export function getReceiptColumns() {
  const colArr = [
    {
      label: "Date", value: true, columnId: "date", width: 95, resizable: true, formatter: formatDateCell
    },
    {
      label: "Document Number", value: true, columnId: "referenceNumber", width: 130, resizable: true, formatter: (value) => String(value || "")
    },
    // Per row rather than in the page header: the statement is drawn for a customer, who may hold
    // several contracts, so each line names the agreement it belongs to instead.
    {
      label: "Contract No", value: true, columnId: "contractNumber", width: 110, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Type", value: true, columnId: "type", width: 115, resizable: true, formatter: (value) => String(value || "").trim()
    },
    {
      label: "Description", value: true, columnId: "description", width: 160, resizable: true, formatter: (value) => String(value || "")
    },
    // Debit and Credit keep the truthiness test on purpose: a zero on one side of a movement
    // means "nothing on this side", so blanking it is what makes the two columns readable.
    {
      label: "Debit", value: true, columnId: "debit", width: 130, resizable: true, formatter: (value) => (value ? formatCurrency(String(value || "")) : "")
    },
    {
      label: "Credit", value: true, columnId: "credit", width: 130, resizable: true, formatter: (value) => (value ? formatCurrency(String(value || "")) : "")
    },
    {
      label: "Balance", value: true, columnId: "balance", width: 130, resizable: true, formatter: formatMoney
    },
    // The last four columns must stay Debit / Credit / Balance / Running Balance: the totals footer
    // in getReceiptRows pads by columns.length - (totals.length + 1) and fills the tail.
    {
      label: "Running Balance", value: true, columnId: "runningBalance", width: 140, resizable: true, formatter: formatMoney
    },
  ];

  return colArr;
}

// Memo block: cheques the business is holding. Not a receivable movement — a collected cheque has
// deliberately not reduced the balance — so this is a disclosure beneath the statement rather than
// rows within it.
//
// Widths are explicit rather than taken from estimatedColumnWidth: its semantic buckets put Bank on
// 350px and every reference on 180px, which pushed the block to 1350px and clipped Amount — the one
// figure the block exists to report — off the right edge. PDC Ref is dropped for the same reason:
// it is the internal document number, and the cheque number beside it is what the customer knows.
export function getPdcColumns() {
  return [
    {
      label: "Cheque No", value: true, columnId: "chequeNo", width: 130, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Cheque Date", value: true, columnId: "chequeDate", width: 130, resizable: true, formatter: formatDateCell
    },
    {
      label: "Due Date", value: true, columnId: "dueDate", width: 130, resizable: true, formatter: formatDateCell
    },
    {
      label: "Bank", value: true, columnId: "bank", width: 220, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Contract No", value: true, columnId: "contractNumber", width: 150, resizable: true, formatter: (value) => String(value || "")
    },
    {
      label: "Amount", value: true, summable: true, columnId: "amount", width: 160, resizable: true, formatter: formatAmount
    },
  ];
}

// The invoices section: one row per rental installment and one per general invoice.
//
// What settled a line is split three ways rather than lumped into one Paid/Adjusted figure,
// because the three are not the same kind of thing and only two of them reduce the balance:
//   Paid        - cash, transfer, and cheques that have cleared. Reduces Balance.
//   Credit Note - value written off against the line. Reduces Balance.
//   PDC         - cheques collected and not yet banked. Does NOT reduce Balance: a cheque on
//                 hand has not touched the receivable in the ledger, only ClearCheque does
//                 that. It sits beside the balance as a disclosure of how much of what is
//                 still outstanding is already covered by cheques in hand.
// Balance is therefore Total - Paid - Credit Note, and reading it next to PDC tells you the
// same story the statement of account below tells, instead of contradicting it.
//
// Each amount is followed by the documents behind it, because "who paid this" is the first
// question asked of any of these three figures and the answer is otherwise a separate lookup.
// A single line is commonly settled by more than one document, so these cells carry lists.
//
// Explicit widths, as on the statement above, because estimatedColumnWidth() buckets by name and
// puts Description on 350px. These add to ~1520px, which is wider than the ~1158px grid pane at a
// 1500px window, so this section scrolls horizontally -- accepted deliberately when the columns
// were paired up, since collapsing the references into one column was the alternative. Every
// column is resizable. VAT stays the narrowest money column on purpose: it carries tax on a
// single invoice line, so it is an order of magnitude smaller than the totals beside it.
export function getTenantStatementColumns() {
  const colArr = [
    {
      label: "Description",
      value: true,
      columnId: "description",
      width: 150,
      resizable: true,
      formatter: (value) => String(value || "")
    },
    {
      label: "Invoice No",
      value: true,
      columnId: "invoiceNo",
      width: 105,
      resizable: true,
      formatter: (value) => String(value ?? "")
    },
    // Per row rather than in the page header, for the same reason as the statement above: a
    // customer may hold several agreements, so each line names the one it belongs to. Blank for a
    // General Invoice raised against no contract at all.
    {
      label: "Contract No",
      value: true,
      columnId: "contractNumber",
      width: 100,
      resizable: true,
      formatter: (value) => String(value || "")
    },
    // The invoice's own date, not the installment due date behind it.
    {
      label: "Invoice Date",
      value: true,
      columnId: "transactionDate",
      width: 95,
      resizable: true,
      formatter: formatDateCell
    },
    {
      label: "Amount", value: true, summable: true, columnId: "amount", width: 110, resizable: true, formatter: formatAmount
    },
    {
      label: "VAT",
      value: true,
      summable: true,
      columnId: "vat",
      width: 75,
      resizable: true,
      formatter: formatAmount
    },
    {
      label: "Total", value: true, summable: true, columnId: "total", width: 115, resizable: true, formatter: formatAmount
    },
    // Money that actually reached the receivable, and the receipts that brought it.
    {
      label: "Paid", value: true, summable: true, columnId: "paidAmount", width: 105, resizable: true, formatter: formatAmount
    },
    {
      label: "Receipt No", value: true, columnId: "paidNumbers", width: 120, resizable: true, formatter: formatRefs
    },
    // Value written off against the line, and the credit notes that did it.
    {
      label: "Credit Note", value: true, summable: true, columnId: "creditedAmount", width: 105, resizable: true, formatter: formatAmount
    },
    {
      label: "Credit Note No", value: true, columnId: "creditNoteNumbers", width: 120, resizable: true, formatter: formatRefs
    },
    // Cheques collected and not yet cleared. Reported beside the balance rather than inside it --
    // see the note above the function. The cheque number rather than the PDC document number,
    // because the cheque number is what the customer wrote and what they will ask about.
    {
      label: "PDC", value: true, summable: true, columnId: "pdcAmount", width: 100, resizable: true, formatter: formatAmount
    },
    {
      label: "Cheque No", value: true, columnId: "pdcNumbers", width: 105, resizable: true, formatter: formatRefs
    },
    {
      label: "Balance", value: true, summable: true, columnId: "balance", width: 115, resizable: true, formatter: formatAmount
    }
  ];

  return colArr;
}
